import { type BaseServerMessage, type ServerMessage } from '@soulhx/fs-common';
import { db, type LocalServerMessage } from '../model/db.js';
import { triggerGlobalSync } from './GlobalSyncMiddleware.js';
import { syncFetch } from './syncFetch.js';

export const SERVER_MESSAGE_SYNC_POLL_INTERVAL_MS = 60_000;
export const SERVER_MESSAGE_SYNC_KEY = 'serverMessages';

const SERVER_MESSAGES_PATH = '/comms/sm';

// a row is only rewritten when the server says it changed, so live queries
// over `db.serverMessages` stay quiet between polls. A missing updateDate
// (shouldn't happen) can't prove "unchanged", so it always counts as changed.
const hasChanged = (local: LocalServerMessage | undefined, server: ServerMessage): boolean =>
  !local || !server.updateDate || local.updateDate !== server.updateDate;

/**
 * Pulls the full set of server messages and reconciles dexie against it.
 * Unlike the other sync modules this is a snapshot, not a `since` delta:
 * server messages are hard-deleted server-side (no tombstone a delta could
 * report), and there are only ever a handful of them, so asking for all of
 * them every time is both simpler and cheap. Anything local that's missing
 * from the snapshot has been deleted.
 */
export const pullServerMessages = async (): Promise<void> => {
  const serverMessages = await syncFetch<ServerMessage[]>({ method: 'GET', path: `${SERVER_MESSAGES_PATH}/` });

  await db.transaction('rw', db.serverMessages, async () => {
    const localById = new Map((await db.serverMessages.toArray()).map((m) => [m.id, m]));
    const serverIds = new Set(serverMessages.map((m) => m.id));

    const toDelete = Array.from(localById.keys()).filter((id) => !serverIds.has(id));
    const toPut = serverMessages.filter((m) => hasChanged(localById.get(m.id), m));

    if (toDelete.length > 0) await db.serverMessages.bulkDelete(toDelete);
    if (toPut.length > 0) await db.serverMessages.bulkPut(toPut);
  });
};

// pull-only: server messages are admin-written through the online-only
// helpers below, so there's never a pending local change to push first
export const syncServerMessages = pullServerMessages;

// The write helpers below are online-only (no pendingCreate/etc. queue):
// only admins write server messages, rarely, and the server does validation
// (e.g. duplicate langs) the admin needs to see immediately - so errors
// propagate to the caller rather than being queued for retry. Each one
// writes the server's response straight into dexie, then triggers a re-pull:
// a poll already in flight when the write happened could otherwise land a
// snapshot taken *before* it (e.g. deleting a just-created row) and leave
// dexie stale until the next full poll interval.

export const createServerMessage = async (msg: BaseServerMessage): Promise<ServerMessage> => {
  const saved = await syncFetch<ServerMessage>({ method: 'POST', path: `${SERVER_MESSAGES_PATH}/`, body: msg });
  await db.serverMessages.put(saved);
  triggerGlobalSync(SERVER_MESSAGE_SYNC_KEY);
  return saved;
};

export const updateServerMessage = async (msg: ServerMessage): Promise<ServerMessage> => {
  const saved = await syncFetch<ServerMessage>({
    method: 'PUT',
    path: `${SERVER_MESSAGES_PATH}/${msg.id}`,
    body: msg,
  });
  await db.serverMessages.put(saved);
  triggerGlobalSync(SERVER_MESSAGE_SYNC_KEY);
  return saved;
};

export const deleteServerMessage = async (id: string): Promise<void> => {
  await syncFetch<{ success: boolean }>({ method: 'DELETE', path: `${SERVER_MESSAGES_PATH}/${id}` });
  await db.serverMessages.delete(id);
  triggerGlobalSync(SERVER_MESSAGE_SYNC_KEY);
};
